<template>
  <div class="lp-staking-interface">
    <h2 class="text-h5 mb-6">Staking de Tokens LP</h2>
    
    <!-- Estatísticas gerais -->
    <v-row class="mb-6">
      <v-col cols="12" md="3">
        <v-card class="pa-4 text-center h-100" elevation="1">
          <div class="text-overline mb-1">Total em Staking</div>
          <div class="text-h5 font-weight-bold mb-2">${{ formatNumber(totalStakedValue) }}</div>
          <div class="text-caption text-success">+{{ stakingStats.totalGrowth }}% nos últimos 30 dias</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pa-4 text-center h-100" elevation="1">
          <div class="text-overline mb-1">Seu Staking</div>
          <div class="text-h5 font-weight-bold mb-2">${{ formatNumber(userStakedValue) }}</div>
          <div class="text-caption">Em {{ userStakedPools.length }} pools diferentes</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pa-4 text-center h-100" elevation="1">
          <div class="text-overline mb-1">Recompensas Pendentes</div>
          <div class="text-h5 font-weight-bold mb-2">{{ formatNumber(pendingRewards) }} {{ coin }}</div>
          <div class="text-caption">≈ ${{ formatNumber(pendingRewardsUsd) }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="pa-4 text-center h-100" elevation="1">
          <div class="text-overline mb-1">APR Médio</div>
          <div class="text-h5 font-weight-bold mb-2">{{ formatNumber(averageApr) }}%</div>
          <div class="text-caption">Inclui stake + taxas</div>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Filtros e busca -->
    <div class="d-flex flex-wrap align-center gap-4 mb-4">
      <v-text-field
        v-model="searchQuery"
        label="Buscar pool"
        density="compact"
        variant="outlined"
        prepend-inner-icon="mdi-magnify"
        hide-details
        class="max-width-300"
        clearable
      ></v-text-field>

      <v-select
        v-model="filterNetwork"
        :items="networks"
        item-title="name"
        item-value="id"
        label="Rede"
        density="compact"
        variant="outlined"
        hide-details
        class="max-width-200"
      >
        <template v-slot:selection="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="20" :color="item.raw.color" class="mr-2">
              <span class="text-caption">{{ item.raw.icon }}</span>
            </v-avatar>
            {{ item.raw.name }}
          </div>
        </template>
      </v-select>

      <v-checkbox
        v-model="showMyStakesOnly"
        label="Apenas meus stakes"
        hide-details
        density="compact"
      ></v-checkbox>
      
      <v-spacer></v-spacer>
      
      <v-btn
        color="primary"
        variant="tonal"
        prepend-icon="mdi-cash-multiple"
        @click="claimAllRewards"
        :disabled="pendingRewards <= 0"
      >
        Reclamar Todas as Recompensas
      </v-btn>
    </div>

    <!-- Abas para diferentes visualizações -->
    <v-tabs v-model="stakingTab" class="mb-4">
      <v-tab value="active">
        <v-icon start>mdi-clock-outline</v-icon>
        Ativos
      </v-tab>
      <v-tab value="my-stakes">
        <v-icon start>mdi-account</v-icon>
        Meus Stakes
      </v-tab>
      <v-tab value="ended">
        <v-icon start>mdi-clock-end</v-icon>
        Encerrados
      </v-tab>
    </v-tabs>

    <v-window v-model="stakingTab">
      <!-- Pools de staking ativos -->
      <v-window-item value="active">
        <v-card v-if="filteredActivePools.length > 0">
          <v-data-table
            :headers="stakingPoolHeaders"
            :items="filteredActivePools"
            :loading="isLoading"
            class="staking-table"
            @click:row="showStakingDetails"
          >
            <!-- Coluna pool -->
            <template v-slot:item.pool="{ item }">
              <div class="d-flex align-center">
                <div class="token-pair-icons position-relative mr-2">
                  <v-avatar size="24" :color="item.raw.pool.token1.color" class="token-icon">
                    <span class="text-caption text-white">{{ item.raw.pool.token1.symbol.charAt(0) }}</span>
                  </v-avatar>
                  <v-avatar size="24" :color="item.raw.pool.token2.color" class="token-icon token-icon-second">
                    <span class="text-caption text-white">{{ item.raw.pool.token2.symbol.charAt(0) }}</span>
                  </v-avatar>
                </div>
                <div>
                  <div class="font-weight-medium">{{ item.raw.pool.token1.symbol }}/{{ item.raw.pool.token2.symbol }}</div>
                  <div class="text-caption">{{ getNetworkName(item.raw.network) }}</div>
                </div>
              </div>
            </template>
            
            <!-- Coluna tipo de staking -->
            <template v-slot:item.type="{ item }">
              <v-chip
                :color="getStakingTypeColor(item.raw.type)"
                size="small"
                class="text-uppercase"
              >
                {{ getStakingTypeLabel(item.raw.type) }}
              </v-chip>
            </template>
            
            <!-- Coluna APR -->
            <template v-slot:item.apr="{ item }">
              <div>
                <div class="d-flex align-center">
                  <span :class="item.raw.apr >= 20 ? 'text-success font-weight-bold' : ''">
                    {{ formatNumber(item.raw.apr) }}%
                  </span>
                  <v-tooltip text="APR do staking + taxas da pool">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small" class="ml-1">mdi-information-outline</v-icon>
                    </template>
                  </v-tooltip>
                </div>
                <div class="text-caption">
                  Stake: {{ formatNumber(item.raw.baseApr) }}% + Taxas: {{ formatNumber(item.raw.feeApr) }}%
                </div>
              </div>
            </template>
            
            <!-- Coluna TVL -->
            <template v-slot:item.tvl="{ item }">
              <div>${{ formatNumber(item.raw.tvl) }}</div>
            </template>
            
            <!-- Coluna duração -->
            <template v-slot:item.duration="{ item }">
              <div>{{ formatDuration(item.raw.duration) }}</div>
              <div class="text-caption" v-if="item.raw.endDate">
                Até {{ formatDate(item.raw.endDate) }}
              </div>
            </template>
            
            <!-- Coluna status -->
            <template v-slot:item.status="{ item }">
              <v-chip
                :color="getStatusColor(item.raw.status)"
                size="small"
                class="text-uppercase"
              >
                <v-icon start size="x-small">{{ getStatusIcon(item.raw.status) }}</v-icon>
                {{ getStatusLabel(item.raw.status) }}
              </v-chip>
            </template>
            
            <!-- Coluna ações -->
            <template v-slot:item.actions="{ item }">
              <div class="d-flex">
                <v-tooltip text="Fazer Stake">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      variant="text"
                      color="primary"
                      size="small"
                      icon="mdi-safe-square"
                      v-bind="props"
                      @click.stop="openStakeDialog(item.raw)"
                      class="mr-1"
                    ></v-btn>
                  </template>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </v-card>
        <v-card v-else class="pa-6 text-center">
          <v-icon size="64" color="grey" class="mb-4">mdi-database-off-outline</v-icon>
          <h3 class="text-h5 mb-2">Nenhuma pool de staking encontrada</h3>
          <p class="text-body-1 mb-4">Não existem pools de staking ativos que correspondam aos filtros aplicados.</p>
        </v-card>
      </v-window-item>
      
      <!-- Meus stakes -->
      <v-window-item value="my-stakes">
        <v-card v-if="filteredUserStakes.length > 0">
          <v-data-table
            :headers="myStakesHeaders"
            :items="filteredUserStakes"
            :loading="isLoading"
            class="staking-table"
          >
            <!-- Templates das colunas similares aos das pools ativas com informações específicas para stakes do usuário -->
          </v-data-table>
        </v-card>
        <v-card v-else class="pa-6 text-center">
          <v-icon size="64" color="grey" class="mb-4">mdi-safe-square-outline</v-icon>
          <h3 class="text-h5 mb-2">Você ainda não possui tokens LP em staking</h3>
          <p class="text-body-1 mb-4">Participe de uma pool de liquidez e faça stake dos seus tokens LP para ganhar recompensas adicionais.</p>
        </v-card>
      </v-window-item>
      
      <!-- Pools de staking encerrados -->
      <v-window-item value="ended">
        <v-card>
          <v-data-table
            :headers="stakingPoolHeaders"
            :items="filteredEndedPools"
            :loading="isLoading"
            class="staking-table"
          >
            <!-- Templates das colunas similares aos das pools ativas -->
          </v-data-table>
        </v-card>
      </v-window-item>
    </v-window>
  </div>
</template>

<script>
import { useDexStore } from '@/stores/dex'
import { useAppStore } from '@/stores/app'

export default {
  name: 'LpStakingInterface',
  
  inject: ['openStakingDialog'],
  
  data() {
    return {
      isLoading: false,
      searchQuery: '',
      filterNetwork: null,
      showMyStakesOnly: false,
      stakingTab: 'active',
      
      stakingPoolHeaders: [
        { title: 'Pool', key: 'pool', align: 'start' },
        { title: 'Tipo', key: 'type', align: 'start' },
        { title: 'APR', key: 'apr', align: 'end' },
        { title: 'TVL', key: 'tvl', align: 'end' },
        { title: 'Duração', key: 'duration', align: 'center' },
        { title: 'Status', key: 'status', align: 'center' },
        { title: 'Ações', key: 'actions', align: 'center', sortable: false }
      ],
      
      myStakesHeaders: [
        { title: 'Pool', key: 'pool', align: 'start' },
        { title: 'Valor Staking', key: 'stakedValue', align: 'end' },
        { title: 'APR', key: 'apr', align: 'end' },
        { title: 'Recompensas', key: 'rewards', align: 'end' },
        { title: 'Bloqueado até', key: 'unlockDate', align: 'center' },
        { title: 'Ações', key: 'actions', align: 'center', sortable: false }
      ],
      
      networks: [
        { id: null, name: 'Todas as redes', color: 'grey', icon: 'T' },
        { id: 'quarnx', name: 'QuarnX', color: 'primary', icon: 'Q' },
        { id: 'ethereum', name: 'Ethereum', color: '#6B7FFF', icon: 'E' },
        { id: 'bsc', name: 'BSC', color: '#F0B90B', icon: 'B' },
        { id: 'polkadot', name: 'Polkadot', color: '#E6007A', icon: 'P' }
      ],
      
      // Mock data para stakings ativos
      stakingPools: [
        {
          id: 1,
          pool: {
            id: 1,
            token1: { symbol: window.__QUARNX_COIN__ || 'tKinX', color: 'primary' },
            token2: { symbol: 'QUSD', color: '#6BBF4B' }
          },
          network: 'quarnx',
          type: 'flexible',
          apr: 28.5,
          baseApr: 22.0,
          feeApr: 6.5,
          tvl: 1250400,
          duration: 0, // Flexível, sem duração fixa
          endDate: null,
          status: 'active',
          minStake: 10,
          userStaked: 0
        },
        {
          id: 2,
          pool: {
            id: 2,
            token1: { symbol: window.__QUARNX_COIN__ || 'tKinX', color: 'primary' },
            token2: { symbol: 'ETH', color: '#6B7FFF' }
          },
          network: 'quarnx',
          type: 'locked',
          apr: 35.2,
          baseApr: 30.0,
          feeApr: 5.2,
          tvl: 785300,
          duration: 90 * 24 * 60 * 60, // 90 dias em segundos
          endDate: new Date('2025-07-22'),
          status: 'active',
          minStake: 50,
          userStaked: 850
        },
        {
          id: 3,
          pool: {
            id: 3,
            token1: { symbol: window.__QUARNX_COIN__ || 'tKinX', color: 'primary' },
            token2: { symbol: 'BNB', color: '#F0B90B' }
          },
          network: 'quarnx',
          type: 'locked',
          apr: 42.5,
          baseApr: 37.0,
          feeApr: 5.5,
          tvl: 520700,
          duration: 180 * 24 * 60 * 60, // 180 dias em segundos
          endDate: new Date('2025-10-22'),
          status: 'active',
          minStake: 100,
          userStaked: 0
        }
      ],
      
      // Mock data para stakes do usuário
      userStakes: [
        {
          id: 1,
          stakingPoolId: 2,
          pool: {
            id: 2,
            token1: { symbol: window.__QUARNX_COIN__ || 'tKinX', color: 'primary' },
            token2: { symbol: 'ETH', color: '#6B7FFF' }
          },
          stakedValue: 850,
          stakedAmount: 0.42, // Em tokens LP
          apr: 35.2,
          rewards: 12.5,
          startDate: new Date('2025-04-15'),
          unlockDate: new Date('2025-07-14'),
          network: 'quarnx',
          status: 'active'
        }
      ],
      
      // Estatísticas gerais
      stakingStats: {
        totalGrowth: 15.2
      }
    }
  },
  
  computed: {
    dexStore() {
      return useDexStore()
    },
    
    appStore() {
      return useAppStore()
    },
    
    filteredActivePools() {
      let result = this.stakingPools.filter(pool => pool.status === 'active')
      
      // Filtro por rede
      if (this.filterNetwork) {
        result = result.filter(pool => pool.network === this.filterNetwork)
      }
      
      // Filtro por termo de busca
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        result = result.filter(pool => 
          pool.pool.token1.symbol.toLowerCase().includes(query) || 
          pool.pool.token2.symbol.toLowerCase().includes(query)
        )
      }
      
      // Filtro por stakes do usuário
      if (this.showMyStakesOnly) {
        result = result.filter(pool => pool.userStaked > 0)
      }
      
      return result
    },
    
    filteredUserStakes() {
      let result = this.userStakes
      
      // Filtro por rede
      if (this.filterNetwork) {
        result = result.filter(stake => stake.network === this.filterNetwork)
      }
      
      // Filtro por termo de busca
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        result = result.filter(stake => 
          stake.pool.token1.symbol.toLowerCase().includes(query) || 
          stake.pool.token2.symbol.toLowerCase().includes(query)
        )
      }
      
      return result
    },
    
    filteredEndedPools() {
      let result = this.stakingPools.filter(pool => pool.status === 'ended')
      
      // Filtros similares aos pools ativos
      
      return result
    },
    
    totalStakedValue() {
      return this.stakingPools.reduce((sum, pool) => sum + pool.tvl, 0)
    },
    
    userStakedValue() {
      return this.userStakes.reduce((sum, stake) => sum + stake.stakedValue, 0)
    },
    
    pendingRewards() {
      return this.userStakes.reduce((sum, stake) => sum + stake.rewards, 0)
    },
    
    pendingRewardsUsd() {
      // Valor USD estimado das recompensas (KinX a $2.5)
      return this.pendingRewards * 2.5
    },
    
    averageApr() {
      if (this.stakingPools.length === 0) return 0
      
      const totalApr = this.stakingPools.reduce((sum, pool) => sum + pool.apr, 0)
      return totalApr / this.stakingPools.length
    },
    
    userStakedPools() {
      // Retorna apenas os pools únicos onde o usuário tem stake
      const poolIds = new Set(this.userStakes.map(stake => stake.stakingPoolId))
      return Array.from(poolIds)
    }
  },
  
  methods: {
    formatNumber(value) {
      return parseFloat(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    formatDate(date) {
      return new Date(date).toLocaleDateString('pt-BR')
    },
    
    formatDuration(seconds) {
      if (seconds === 0) return 'Flexível'
      
      const days = Math.floor(seconds / (24 * 60 * 60))
      
      if (days % 30 === 0) {
        const months = days / 30
        return months === 1 ? '1 mês' : `${months} meses`
      } else {
        return days === 1 ? '1 dia' : `${days} dias`
      }
    },
    
    getNetworkName(networkId) {
      const network = this.networks.find(n => n.id === networkId)
      return network ? network.name : 'Desconhecida'
    },
    
    getNetworkColor(networkId) {
      const network = this.networks.find(n => n.id === networkId)
      return network ? network.color : 'grey'
    },
    
    getNetworkIcon(networkId) {
      const network = this.networks.find(n => n.id === networkId)
      return network ? network.icon : '?'
    },
    
    getStakingTypeLabel(type) {
      const types = {
        'flexible': 'Flexível',
        'locked': 'Bloqueado'
      }
      return types[type] || type
    },
    
    getStakingTypeColor(type) {
      const colors = {
        'flexible': 'info',
        'locked': 'warning'
      }
      return colors[type] || 'grey'
    },
    
    getStatusLabel(status) {
      const statuses = {
        'active': 'Ativo',
        'ended': 'Encerrado',
        'coming': 'Em breve'
      }
      return statuses[status] || status
    },
    
    getStatusColor(status) {
      const colors = {
        'active': 'success',
        'ended': 'grey',
        'coming': 'warning'
      }
      return colors[status] || 'grey'
    },
    
    getStatusIcon(status) {
      const icons = {
        'active': 'mdi-check-circle',
        'ended': 'mdi-clock-end',
        'coming': 'mdi-clock-start'
      }
      return icons[status] || 'mdi-help-circle-outline'
    },
    
    showStakingDetails(item) {
      // Mostrar detalhes da pool de staking
      const stakingPool = item.raw
      const userStake = this.userStakes.find(stake => stake.stakingPoolId === stakingPool.id)
      
      if (userStake) {
        // Se o usuário já tem stake, mostrar detalhes do stake
        this.openStakingDialog(stakingPool, userStake, 'info')
      } else {
        // Se o usuário não tem stake, abrir diálogo para fazer stake
        this.openStakingDialog(stakingPool, null, 'stake')
      }
    },
    
    openStakeDialog(stakingPool) {
      // Abrir diálogo para fazer stake
      this.openStakingDialog(stakingPool, null, 'stake')
    },
    
    claimAllRewards() {
      // Reclamar todas as recompensas pendentes
      this.appStore.showSnackbar({
        text: `Reclamando ${this.formatNumber(this.pendingRewards)} KinX de recompensas...`,
        color: 'success'
      })
      
      // Simular sucesso após delay
      setTimeout(() => {
        // Zerar as recompensas após reclamar
        this.userStakes.forEach(stake => {
          stake.rewards = 0
        })
        
        this.appStore.showSnackbar({
          text: `Recompensas reclamadas com sucesso!`,
          color: 'success'
        })
      }, 2000)
    }
  },
  
  mounted() {
    this.isLoading = true
    
    // Simulação de carregamento
    setTimeout(() => {
      this.isLoading = false
    }, 1000)
  }
}
</script>

<style scoped>
.lp-staking-interface {
  width: 100%;
}

.token-icon {
  z-index: 1;
}

.token-icon-second {
  margin-left: -12px;
}

.max-width-300 {
  max-width: 300px;
}

.max-width-200 {
  max-width: 200px;
}

.staking-table {
  cursor: pointer;
}

.gap-4 {
  gap: 16px;
}
</style>
